"use client";

import React from "react";
import { Database, FileText, Shield, Key, CircleCheck, Quote, Settings2, Layers3 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const policies = [{
  id: "policy-isolation",
  title: "Strict tenant isolation",
  description: "Keep tenant data and retrieval boundaries separated across managed environments.",
  icon: Shield,
  defaultState: true
}, {
  id: "policy-byok",
  title: "BYO-Key enforcement",
  description: "Require tenants to provide their own LLM and OCR keys instead of relying on platform fallback keys.",
  icon: Key,
  defaultState: true
}, {
  id: "policy-approved",
  title: "Approved content only",
  description: "Restrict answers to content that has passed your governance workflow and reached Approved status.",
  icon: CircleCheck,
  defaultState: true
}, {
  id: "policy-citations",
  title: "Mandatory citations",
  description: "Require exact source support for responses and refuse when that evidence is not available.",
  icon: Quote,
  defaultState: true
}, {
  id: "policy-logging",
  title: "Audit logging",
  description: "Keep traceable logs for queries, retrieval behavior, and configuration changes.",
  icon: FileText,
  defaultState: false
}, {
  id: "policy-retention",
  title: "Retention limits",
  description: "Set rules for temporary files, cached OCR output, and interaction history retention.",
  icon: Database,
  defaultState: false
}];
export default function PolicyControls() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="policy-controls" className="bg-background py-16 lg:py-20 overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-12 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Layers3 className="size-4" />
            <span>Policy controls</span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-6">
            Granular controls for governed RAG delivery
          </h2>

          <p className="text-lg text-muted-foreground leading-relaxed">
            Apply operational rules across tenants and environments from a centralized policy layer
            designed for controlled retrieval and answer behavior.
          </p>
        </div>

        <div ref={gridRef} className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-1000 ease-out delay-200", isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          {policies.map((policy, i) => {
          const Icon = policy.icon;
          return <Card key={policy.id} data-index={i} className="flex flex-col h-full border-border shadow-sm hover:shadow-md transition-shadow duration-300 bg-card">
                <CardHeader className="pb-6 flex-grow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-secondary rounded-lg text-foreground border border-border/50">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground">
                      {policy.title}
                    </CardTitle>
                  </div>
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                    {policy.description}
                  </CardDescription>
                </CardHeader>

                <CardFooter className="pt-4 pb-5 border-t border-border/50 flex items-center justify-between bg-secondary/30 rounded-b-xl mt-auto">
                  <Label htmlFor={policy.id} className="text-sm font-medium text-foreground cursor-pointer select-none">
                    Enforce Policy
                  </Label>
                  <Switch id={policy.id} defaultChecked={policy.defaultState} aria-label={`Toggle ${policy.title}`} />
                </CardFooter>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}